/**
 * Sampling for point-defined shapes:
 * - 'polygon': ordered outline points spanning a (possibly concave) face
 * - 'hull':    unordered points spanning their convex hull, filled or shell
 *
 * Coplanar 'hull' input degrades gracefully to the planar convex hull face.
 * All coordinates are flat xyz triplets in authored (editor world) space.
 */

import { buildHull, hullArea, hullVolume, insideHull, HullFace } from './hull';
import { convexHull2d, fitPlane, insidePolygon2d, Plane, projectToPlane, signedArea2d, triangulate, unprojectFromPlane } from './polygon';
import { Rng } from './rng';

// sample writer shape matching sampling.ts (structural type to avoid a cycle)
interface Writer {
    push(x: number, y: number, z: number, nx?: number, ny?: number, nz?: number): void;
}

// plane through (roughly) coplanar, unordered points: two extreme points,
// the point farthest from their line, normal from the cross product
const planarBasis = (points: number[]): Plane | null => {
    const n = points.length / 3;
    if (n < 3) {
        return null;
    }

    const o: [number, number, number] = [0, 0, 0];
    for (let i = 0; i < n; i++) {
        o[0] += points[i * 3];
        o[1] += points[i * 3 + 1];
        o[2] += points[i * 3 + 2];
    }
    o[0] /= n;
    o[1] /= n;
    o[2] /= n;

    let i0 = 0;
    let i1 = 0;
    for (let axis = 0; axis < 3 && i0 === i1; axis++) {
        for (let i = 1; i < n; i++) {
            if (points[i * 3 + axis] < points[i0 * 3 + axis]) i0 = i;
            if (points[i * 3 + axis] > points[i1 * 3 + axis]) i1 = i;
        }
    }
    const ux = points[i1 * 3] - points[i0 * 3];
    const uy = points[i1 * 3 + 1] - points[i0 * 3 + 1];
    const uz = points[i1 * 3 + 2] - points[i0 * 3 + 2];
    const ulen = Math.hypot(ux, uy, uz);
    if (ulen < 1e-12) {
        return null;
    }
    const u: [number, number, number] = [ux / ulen, uy / ulen, uz / ulen];

    let best = 1e-12;
    let nrm: [number, number, number] | null = null;
    for (let i = 0; i < n; i++) {
        const wx = points[i * 3] - points[i0 * 3];
        const wy = points[i * 3 + 1] - points[i0 * 3 + 1];
        const wz = points[i * 3 + 2] - points[i0 * 3 + 2];
        const cx = u[1] * wz - u[2] * wy;
        const cy = u[2] * wx - u[0] * wz;
        const cz = u[0] * wy - u[1] * wx;
        const clen = Math.hypot(cx, cy, cz);
        if (clen > best) {
            best = clen;
            nrm = [cx / clen, cy / clen, cz / clen];
        }
    }
    if (!nrm) {
        return null;  // collinear
    }
    const v: [number, number, number] = [
        nrm[1] * u[2] - nrm[2] * u[1],
        nrm[2] * u[0] - nrm[0] * u[2],
        nrm[0] * u[1] - nrm[1] * u[0]
    ];
    return { o, u, v, n: nrm };
};

type FaceGeometry = {
    plane: Plane;
    outline2d: number[];   // flat xy pairs, ordered outline
    triangles: number[];   // index triples into outline2d
    area: number;
};

// geometry of an ordered (possibly concave) outline
const polygonGeometry = (points: number[]): FaceGeometry | null => {
    const plane = fitPlane(points) ?? planarBasis(points);
    if (!plane) {
        return null;
    }
    const outline2d = projectToPlane(points, plane);
    const triangles = triangulate(outline2d);
    if (triangles.length === 0) {
        return null;
    }
    return { plane, outline2d, triangles, area: Math.abs(signedArea2d(outline2d)) };
};

// planar convex hull face of unordered (roughly) coplanar points
const planarHullGeometry = (points: number[]): FaceGeometry | null => {
    const plane = planarBasis(points);
    if (!plane) {
        return null;
    }
    const all2d = projectToPlane(points, plane);
    const hull = convexHull2d(all2d);
    if (hull.length < 3) {
        return null;
    }
    const outline2d: number[] = [];
    for (const i of hull) {
        outline2d.push(all2d[i * 2], all2d[i * 2 + 1]);
    }
    const triangles = triangulate(outline2d);
    if (triangles.length === 0) {
        return null;
    }
    return { plane, outline2d, triangles, area: Math.abs(signedArea2d(outline2d)) };
};

type HullGeometry = {
    faces: HullFace[];
    area: number;
    volume: number;
};

const hullGeometry = (points: number[]): HullGeometry | null => {
    const faces = buildHull(points);
    if (!faces) {
        return null;
    }
    return { faces, area: hullArea(points, faces), volume: hullVolume(points, faces) };
};

// uniform sample in the triangle spanned by outline indices a/b/c
const sampleFaceTriangle = (
    writer: Writer, rng: Rng, geo: FaceGeometry, a: number, b: number, c: number
) => {
    const su = Math.sqrt(rng());
    const v = rng();
    const w0 = 1 - su;
    const w1 = su * (1 - v);
    const w2 = su * v;
    const x = geo.outline2d[a * 2] * w0 + geo.outline2d[b * 2] * w1 + geo.outline2d[c * 2] * w2;
    const y = geo.outline2d[a * 2 + 1] * w0 + geo.outline2d[b * 2 + 1] * w1 + geo.outline2d[c * 2 + 1] * w2;
    const p = unprojectFromPlane(x, y, geo.plane);
    writer.push(p[0], p[1], p[2], geo.plane.n[0], geo.plane.n[1], geo.plane.n[2]);
};

// sample a planar face: jittered/exact grid clipped by the outline, or
// area-weighted random over the triangulation
const sampleFace = (writer: Writer, rng: Rng, gridJitter: number | null, density: number, geo: FaceGeometry) => {
    if (gridJitter !== null) {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        const count = geo.outline2d.length / 2;
        for (let i = 0; i < count; i++) {
            minX = Math.min(minX, geo.outline2d[i * 2]);
            maxX = Math.max(maxX, geo.outline2d[i * 2]);
            minY = Math.min(minY, geo.outline2d[i * 2 + 1]);
            maxY = Math.max(maxY, geo.outline2d[i * 2 + 1]);
        }
        const cell = 1 / Math.sqrt(density);
        const nu = Math.max(1, Math.round((maxX - minX) / cell));
        const nv = Math.max(1, Math.round((maxY - minY) / cell));
        const du = (maxX - minX) / nu;
        const dv = (maxY - minY) / nv;
        for (let i = 0; i < nu; i++) {
            for (let j = 0; j < nv; j++) {
                const x = minX + (i + 0.5 + (rng() - 0.5) * 2 * gridJitter) * du;
                const y = minY + (j + 0.5 + (rng() - 0.5) * 2 * gridJitter) * dv;
                if (insidePolygon2d(geo.outline2d, x, y)) {
                    const p = unprojectFromPlane(x, y, geo.plane);
                    writer.push(p[0], p[1], p[2], geo.plane.n[0], geo.plane.n[1], geo.plane.n[2]);
                }
            }
        }
    } else {
        const n = Math.max(1, Math.ceil(density * geo.area));
        // area-weighted triangle selection
        const triCount = geo.triangles.length / 3;
        const cumulative = new Float64Array(triCount);
        let total = 0;
        const x = (i: number) => geo.outline2d[i * 2];
        const y = (i: number) => geo.outline2d[i * 2 + 1];
        for (let t = 0; t < triCount; t++) {
            const [a, b, c] = [geo.triangles[t * 3], geo.triangles[t * 3 + 1], geo.triangles[t * 3 + 2]];
            total += Math.abs((x(b) - x(a)) * (y(c) - y(a)) - (x(c) - x(a)) * (y(b) - y(a))) / 2;
            cumulative[t] = total;
        }
        for (let i = 0; i < n; i++) {
            const r = rng() * total;
            let t = 0;
            while (t < triCount - 1 && cumulative[t] < r) {
                t++;
            }
            sampleFaceTriangle(writer, rng, geo, geo.triangles[t * 3], geo.triangles[t * 3 + 1], geo.triangles[t * 3 + 2]);
        }
    }
};

// sample the volume of a convex hull: jittered/exact grid over the bounding
// box clipped by the hull planes, or volume-weighted random over the
// centroid-fan tetrahedra
const sampleHullVolume = (
    writer: Writer, rng: Rng, gridJitter: number | null, density: number,
    points: number[], geo: HullGeometry
) => {
    if (gridJitter !== null) {
        const min = [Infinity, Infinity, Infinity];
        const max = [-Infinity, -Infinity, -Infinity];
        const n = points.length / 3;
        for (let i = 0; i < n; i++) {
            for (let axis = 0; axis < 3; axis++) {
                min[axis] = Math.min(min[axis], points[i * 3 + axis]);
                max[axis] = Math.max(max[axis], points[i * 3 + axis]);
            }
        }
        const cell = 1 / Math.cbrt(density);
        const counts = [0, 1, 2].map(axis => Math.max(1, Math.round((max[axis] - min[axis]) / cell)));
        const deltas = [0, 1, 2].map(axis => (max[axis] - min[axis]) / counts[axis]);
        for (let i = 0; i < counts[0]; i++) {
            for (let j = 0; j < counts[1]; j++) {
                for (let k = 0; k < counts[2]; k++) {
                    const x = min[0] + (i + 0.5 + (rng() - 0.5) * 2 * gridJitter) * deltas[0];
                    const y = min[1] + (j + 0.5 + (rng() - 0.5) * 2 * gridJitter) * deltas[1];
                    const z = min[2] + (k + 0.5 + (rng() - 0.5) * 2 * gridJitter) * deltas[2];
                    if (insideHull(geo.faces, x, y, z)) {
                        writer.push(x, y, z);
                    }
                }
            }
        }
    } else {
        // centroid of the hull vertices as fan apex
        const n = points.length / 3;
        let cx = 0;
        let cy = 0;
        let cz = 0;
        for (let i = 0; i < n; i++) {
            cx += points[i * 3];
            cy += points[i * 3 + 1];
            cz += points[i * 3 + 2];
        }
        cx /= n;
        cy /= n;
        cz /= n;

        // volume-weighted tetra selection (face triangle + centroid)
        const cumulative = new Float64Array(geo.faces.length);
        let total = 0;
        geo.faces.forEach((f, t) => {
            const ax = points[f.a * 3] - cx;
            const ay = points[f.a * 3 + 1] - cy;
            const az = points[f.a * 3 + 2] - cz;
            const bx = points[f.b * 3] - cx;
            const by = points[f.b * 3 + 1] - cy;
            const bz = points[f.b * 3 + 2] - cz;
            const dx = points[f.c * 3] - cx;
            const dy = points[f.c * 3 + 1] - cy;
            const dz = points[f.c * 3 + 2] - cz;
            total += Math.abs(ax * (by * dz - bz * dy) - ay * (bx * dz - bz * dx) + az * (bx * dy - by * dx)) / 6;
            cumulative[t] = total;
        });

        const count = Math.max(1, Math.ceil(density * geo.volume));
        for (let i = 0; i < count; i++) {
            const r = rng() * total;
            let t = 0;
            while (t < geo.faces.length - 1 && cumulative[t] < r) {
                t++;
            }
            const f = geo.faces[t];

            // uniform barycentric sample in the tetrahedron (folding method)
            let s = rng();
            let u = rng();
            let w = rng();
            if (s + u > 1) {
                s = 1 - s;
                u = 1 - u;
            }
            if (u + w > 1) {
                const tmp = w;
                w = 1 - s - u;
                u = 1 - tmp;
            } else if (s + u + w > 1) {
                const tmp = w;
                w = s + u + w - 1;
                s = 1 - u - tmp;
            }
            const a = 1 - s - u - w;
            writer.push(
                cx * a + points[f.a * 3] * s + points[f.b * 3] * u + points[f.c * 3] * w,
                cy * a + points[f.a * 3 + 1] * s + points[f.b * 3 + 1] * u + points[f.c * 3 + 1] * w,
                cz * a + points[f.a * 3 + 2] * s + points[f.b * 3 + 2] * u + points[f.c * 3 + 2] * w
            );
        }
    }
};

// raster a single hull face: a 2d grid in the face plane, anchored at the
// world origin so rasters line up seamlessly across coplanar faces
const sampleFaceGrid = (
    writer: Writer, rng: Rng, gridJitter: number, density: number,
    points: number[], f: HullFace, thickness: number
) => {
    // in-plane basis from the face normal (same axis pick as polygon.ts,
    // so identical normals yield identical bases)
    const pick = Math.abs(f.nx) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    let ux = pick[1] * f.nz - pick[2] * f.ny;
    let uy = pick[2] * f.nx - pick[0] * f.nz;
    let uz = pick[0] * f.ny - pick[1] * f.nx;
    const ulen = Math.hypot(ux, uy, uz);
    ux /= ulen;
    uy /= ulen;
    uz /= ulen;
    const vx = f.ny * uz - f.nz * uy;
    const vy = f.nz * ux - f.nx * uz;
    const vz = f.nx * uy - f.ny * ux;

    // project the triangle corners into the plane basis
    const corners = [f.a, f.b, f.c].map((i) => {
        const x = points[i * 3];
        const y = points[i * 3 + 1];
        const z = points[i * 3 + 2];
        return [x * ux + y * uy + z * uz, x * vx + y * vy + z * vz];
    });

    // signed side of point (px, py) relative to edge a->b
    const edgeSide = (a: number[], b: number[], px: number, py: number) => {
        return (b[0] - a[0]) * (py - a[1]) - (px - a[0]) * (b[1] - a[1]);
    };

    const cell = 1 / Math.sqrt(density);
    const minS = Math.min(corners[0][0], corners[1][0], corners[2][0]);
    const maxS = Math.max(corners[0][0], corners[1][0], corners[2][0]);
    const minT = Math.min(corners[0][1], corners[1][1], corners[2][1]);
    const maxT = Math.max(corners[0][1], corners[1][1], corners[2][1]);

    // winding of the projected triangle decides the inside sign
    const sign = edgeSide(corners[0], corners[1], corners[2][0], corners[2][1]) >= 0 ? 1 : -1;

    // edges with their hull point indices: adjacent faces share an edge in
    // opposite direction, so on-edge raster points are claimed by exactly
    // one face (the one whose edge runs from the lower to the higher index)
    const edges: [number, number, number[], number[]][] = [
        [f.a, f.b, corners[0], corners[1]],
        [f.b, f.c, corners[1], corners[2]],
        [f.c, f.a, corners[2], corners[0]]
    ];

    for (let i = Math.floor(minS / cell); i * cell <= maxS; i++) {
        for (let j = Math.floor(minT / cell); j * cell <= maxT; j++) {
            const s = (i + 0.5 + (rng() - 0.5) * 2 * gridJitter) * cell;
            const t = (j + 0.5 + (rng() - 0.5) * 2 * gridJitter) * cell;

            let inside = true;
            for (const [ia, ib, ca, cb] of edges) {
                // signed distance to the edge; raster points within the
                // tolerance band count as on-edge and are claimed by the
                // face whose edge runs from the lower to the higher index
                const elen = Math.hypot(cb[0] - ca[0], cb[1] - ca[1]);
                const dist = (sign * edgeSide(ca, cb, s, t)) / Math.max(elen, 1e-12);
                const tol = cell * 1e-6;
                if (dist < -tol || (dist <= tol && ia > ib)) {
                    inside = false;
                    break;
                }
            }
            if (!inside) {
                continue;
            }

            const o = f.d + (thickness > 0 ? (rng() - 0.5) * thickness : 0);
            writer.push(
                s * ux + t * vx + f.nx * o,
                s * uy + t * vy + f.ny * o,
                s * uz + t * vz + f.nz * o,
                f.nx, f.ny, f.nz
            );
        }
    }
};

// sample the surface of a convex hull: per-face raster for grid modes,
// area-weighted random over the hull triangles otherwise
const sampleHullSurface = (
    writer: Writer, rng: Rng, gridJitter: number | null, density: number,
    points: number[], geo: HullGeometry, thickness: number
) => {
    if (gridJitter !== null) {
        for (const f of geo.faces) {
            sampleFaceGrid(writer, rng, gridJitter, density, points, f, thickness);
        }
        return;
    }

    const cumulative = new Float64Array(geo.faces.length);
    let total = 0;
    geo.faces.forEach((f, t) => {
        const abx = points[f.b * 3] - points[f.a * 3];
        const aby = points[f.b * 3 + 1] - points[f.a * 3 + 1];
        const abz = points[f.b * 3 + 2] - points[f.a * 3 + 2];
        const acx = points[f.c * 3] - points[f.a * 3];
        const acy = points[f.c * 3 + 1] - points[f.a * 3 + 1];
        const acz = points[f.c * 3 + 2] - points[f.a * 3 + 2];
        total += Math.hypot(aby * acz - abz * acy, abz * acx - abx * acz, abx * acy - aby * acx) / 2;
        cumulative[t] = total;
    });

    const count = Math.max(1, Math.ceil(density * geo.area));
    for (let i = 0; i < count; i++) {
        const r = rng() * total;
        let t = 0;
        while (t < geo.faces.length - 1 && cumulative[t] < r) {
            t++;
        }
        const f = geo.faces[t];
        const su = Math.sqrt(rng());
        const v = rng();
        const w0 = 1 - su;
        const w1 = su * (1 - v);
        const w2 = su * v;
        const o = thickness > 0 ? (rng() - 0.5) * thickness : 0;
        writer.push(
            points[f.a * 3] * w0 + points[f.b * 3] * w1 + points[f.c * 3] * w2 + f.nx * o,
            points[f.a * 3 + 1] * w0 + points[f.b * 3 + 1] * w1 + points[f.c * 3 + 1] * w2 + f.ny * o,
            points[f.a * 3 + 2] * w0 + points[f.b * 3 + 2] * w1 + points[f.c * 3 + 2] * w2 + f.nz * o,
            f.nx, f.ny, f.nz
        );
    }
};

// expected splat count for point shapes; 0 when the input is degenerate
const estimatePointShape = (kind: 'hull' | 'polygon', points: number[] | undefined, filled: boolean, density: number): number => {
    if (!points || points.length < 9) {
        return 0;
    }
    if (kind === 'polygon') {
        const geo = polygonGeometry(points);
        return geo ? Math.ceil(density * geo.area) : 0;
    }
    const hull = hullGeometry(points);
    if (hull) {
        return Math.ceil(density * (filled ? hull.volume : hull.area));
    }
    const planar = planarHullGeometry(points);
    return planar ? Math.ceil(density * planar.area) : 0;
};

// sample a point shape; returns whether the samples carry normals (surface)
const samplePointShape = (
    writer: Writer, rng: Rng, gridJitter: number | null, density: number,
    kind: 'hull' | 'polygon', points: number[] | undefined, filled: boolean, thickness: number
): boolean => {
    if (!points || points.length < 9) {
        return true;
    }
    if (kind === 'polygon') {
        const geo = polygonGeometry(points);
        if (geo) {
            sampleFace(writer, rng, gridJitter, density, geo);
        }
        return true;
    }

    const hull = hullGeometry(points);
    if (!hull) {
        // coplanar input: fall back to the planar convex hull face
        const planar = planarHullGeometry(points);
        if (planar) {
            sampleFace(writer, rng, gridJitter, density, planar);
        }
        return true;
    }
    if (filled) {
        sampleHullVolume(writer, rng, gridJitter, density, points, hull);
        return false;
    }
    sampleHullSurface(writer, rng, gridJitter, density, points, hull, thickness);
    return true;
};

export { estimatePointShape, samplePointShape, polygonGeometry, hullGeometry, planarHullGeometry };
